import * as THREE from "three";
import * as CANNON from "cannon-es";
import { math, TempVec } from "@engine";
import type { AssetService, PhysicsWorld } from "@engine";
import type {
  BossMarker,
  PickupType,
  SegmentTrick,
  Shape,
  ShapeSegment,
} from "./types";
import { shapeOf } from "../config/shapes";
import { APOTHEM, WALL_THICKNESS, sideLengthFor } from "../config/tunnel";
import { createPickupMesh, disposePickupMesh } from "../pickups/PickupView";

export interface PanelInstance {
  mesh: THREE.Mesh;
  body: CANNON.Body;
  panelIndex: number;
}

export interface PickupInstance {
  mesh: THREE.Mesh;
  body: CANNON.Body;
  type: PickupType;
  panelIndex: number;
  zLocal: number;
  // The PickupManager flips this true as soon as the orb is grabbed.
  // The mesh is hidden + the body removed from the physics world; the
  // remaining husk gets disposed when the segment recycles.
  consumed: boolean;
  // Continuous spin (radians) -- update() multiplies dt and accumulates.
  spin: number;
}

// Per-segment kill bar attached when spec.trick === "sweep". The bar
// hovers just inside the inscribed circle on a single panel and gets
// repositioned by SweepFx on every snare. Body is tagged as a wall so
// the existing Player.onWallHit lethal branch handles it for free; we
// also set __sweep so telemetry / FX can distinguish a sweep hit from
// a regular wall graze if they want to.
export interface SweepInstance {
  mesh: THREE.Mesh;
  body: CANNON.Body;
  // Active panel index this frame (-1 before SweepFx has stepped it).
  panelIndex: number;
  // The fraction-of-side-length the bar is currently lerped to. SweepFx
  // tweens this between snare events so the bar visibly slides instead
  // of teleporting.
  lerpAlpha: number;
}

export interface Segment {
  shape: Shape;
  group: THREE.Group;
  panels: PanelInstance[];
  pickups: PickupInstance[];
  speed: number;
  length: number;
  zCenter: number;
  isGate: boolean;
  active: boolean;
  // Optional trick (pulse / mirror / sweep). Drives both the build-
  // time material choice and the per-frame FX systems.
  trick: SegmentTrick | null;
  // Set when trick === "sweep". Owned by the segment lifecycle: built
  // here, added/removed from the physics world by attach()/detach(),
  // re-posed by syncBodies() and animated by SweepFx.update().
  sweep: SweepInstance | null;
  // Boss markers carried over from the spec. The streamer fires
  // onBossStart on attach if `boss` is set, and onBossCleared when
  // the segment with `bossEnd` recycles. Allows hand-authored boss
  // phrases to declare their own intro/outro events without anyone
  // hard-coding chunk arithmetic.
  boss: BossMarker | null;
  bossEnd: boolean;
  // Cached gap set so FX systems can quickly skip gap panels without
  // having to re-derive them from the original spec each frame.
  gaps: number[];
  // Optional sustained camera roll while the player is inside this
  // segment. BankingFx reads this each frame from getSegmentAtPlayer().
  bank: -1 | 0 | 1;
}

const WALL_BODY_TAG = "wall";
const PICKUP_BODY_TAG = "pickup";
const PICKUP_RADIUS = 0.45;
// Sweep bar geometry. The bar is a thin slab that hovers on the
// INNER face of a panel (so it sits in the cavity, not embedded
// in the wall) and spans 80% of the segment length so the player
// can rotate off it before it lands. Width matches the panel side
// length so it perfectly covers one panel face. SweepFx supplies
// the per-frame inner-face offset since it owns the cavity geometry.
const SWEEP_THICKNESS = 0.4;

export function isWallBody(body: CANNON.Body): boolean {
  return (body as CANNON.Body & { __tag?: string }).__tag === WALL_BODY_TAG;
}

export function isPickupBody(body: CANNON.Body): boolean {
  return (body as CANNON.Body & { __tag?: string }).__tag === PICKUP_BODY_TAG;
}

export function panelIndexOf(body: CANNON.Body): number {
  return (body as CANNON.Body & { __panelIndex?: number }).__panelIndex ?? -1;
}

export function segmentOfBody(body: CANNON.Body): Segment | null {
  return (body as CANNON.Body & { __segment?: Segment }).__segment ?? null;
}

export function pickupTypeOfBody(body: CANNON.Body): PickupType | null {
  return (
    (body as CANNON.Body & { __pickupType?: PickupType }).__pickupType ?? null
  );
}

export function pickupInstanceOfBody(body: CANNON.Body): PickupInstance | null {
  return (
    (body as CANNON.Body & { __pickup?: PickupInstance }).__pickup ?? null
  );
}

// True for sweep bar bodies (built when spec.trick === "sweep"). Tagged
// as walls so the lethal branch in Player.onWallHit fires without
// special-casing, but we also flag __sweep so analytics / future FX
// can distinguish sweep deaths from regular wall grazes.
export function isSweepBody(body: CANNON.Body): boolean {
  return (body as CANNON.Body & { __sweep?: boolean }).__sweep === true;
}

export class SegmentFactory {
  private materials = new Map<Shape, THREE.MeshStandardMaterial>();
  // Cloned-from-base materials with a much hotter emissive. Exactly one
  // panel mesh at a time references the highlight material for its shape
  // (the tile currently sitting under the player); TileHighlight swaps
  // the active panel's mesh.material between the base and highlight
  // entries each frame.
  private highlightMaterials = new Map<Shape, THREE.MeshStandardMaterial>();
  // Cloned-from-base materials shared across every panel of every
  // pulse-trick segment of a given shape. PulsePanelsFx animates the
  // shared material's emissiveIntensity on the kick so the whole pulse
  // segment dims together. Non-pulse segments keep using the calm base
  // material untouched.
  private pulseMaterials = new Map<Shape, THREE.MeshStandardMaterial>();
  // Cloned-from-base materials hue-shifted by ~0.5 (complementary) so
  // mirror segments visually read as "different rules apply" before the
  // player even tries to rotate. One material per shape, shared across
  // all mirror segments of that shape.
  private mirrorMaterials = new Map<Shape, THREE.MeshStandardMaterial>();
  // Geometry cache for the sweep bar mesh. Keyed on the side length so
  // the rare case of multi-shape sweeps in one run shares geometry
  // across same-shape segments.
  private sweepGeometries = new Map<string, THREE.BufferGeometry>();
  private sweepMaterial: THREE.MeshStandardMaterial | null = null;
  private geometryCache = new Map<string, THREE.BufferGeometry>();
  private assets: AssetService;
  private physics: PhysicsWorld;

  constructor(assets: AssetService, physics: PhysicsWorld) {
    this.assets = assets;
    this.physics = physics;
    // Eagerly create base + highlight + trick materials for every
    // shape so the FX systems can look them up the very first frame
    // (before any segment of that shape has been built).
    for (const shape of [
      "octagon",
      "square",
      "triangle",
      "pentagon",
      "hexagon",
    ] as Shape[]) {
      this.getMaterial(shape);
      this.getHighlightMaterialByShape(shape);
      this.getPulseBaseMaterialByShape(shape);
      this.getMirrorMaterialByShape(shape);
    }
  }

  getMaterials(): THREE.MeshStandardMaterial[] {
    return Array.from(this.materials.values());
  }

  getMaterialByShape(shape: Shape): THREE.MeshStandardMaterial | undefined {
    return this.materials.get(shape);
  }

  // Returns the per-shape "active tile" material. Only the panel under
  // the player should be assigned this material; everyone else stays on
  // the dim base material so it reads as silhouette.
  getHighlightMaterialByShape(
    shape: Shape
  ): THREE.MeshStandardMaterial | undefined {
    let m = this.highlightMaterials.get(shape);
    if (m) return m;
    const base = this.getMaterial(shape);
    m = base.clone();
    // Bright but bloom-friendly. TileHighlight modulates this each
    // frame on the audio low band + gate boost, so this is just the
    // baseline anchor the modulator falls back to between beats.
    m.emissiveIntensity = 1.2;
    this.highlightMaterials.set(shape, m);
    return m;
  }

  // Returns the per-shape "pulse" base material used by trick="pulse"
  // segments. PulsePanelsFx writes emissiveIntensity each frame; cloning
  // off the regular base means non-pulse segments stay perfectly calm.
  getPulseBaseMaterialByShape(
    shape: Shape
  ): THREE.MeshStandardMaterial {
    let m = this.pulseMaterials.get(shape);
    if (m) return m;
    const base = this.getMaterial(shape);
    m = base.clone();
    // Same baseline as the calm material; PulsePanelsFx will dive it
    // toward 0.04 on the kick and lerp back here over ~200ms.
    m.emissiveIntensity = 0.18;
    this.pulseMaterials.set(shape, m);
    return m;
  }

  // Enumerated for PulsePanelsFx so it can iterate without re-deriving
  // shape order. One material per shape (shared across all that shape's
  // pulse-trick panels), so this is at most three entries.
  getPulseMaterials(): THREE.MeshStandardMaterial[] {
    return Array.from(this.pulseMaterials.values());
  }

  // Returns the per-shape mirror material. Hue is shifted ~half a turn
  // around the colour wheel so the player visually clocks "this segment
  // plays differently" before the first rotate input is even pressed.
  getMirrorMaterialByShape(
    shape: Shape
  ): THREE.MeshStandardMaterial {
    let m = this.mirrorMaterials.get(shape);
    if (m) return m;
    const base = this.getMaterial(shape);
    m = base.clone();
    m.color = base.color.clone();
    m.emissive = base.emissive.clone();
    m.color.offsetHSL(0.5, 0, 0);
    m.emissive.offsetHSL(0.5, 0, 0);
    // Slightly hotter so the contrast is unmistakable.
    m.emissiveIntensity = 0.35;
    this.mirrorMaterials.set(shape, m);
    return m;
  }

  build(spec: ShapeSegment, zCenter: number): Segment {
    const cfg = shapeOf(spec.shape);
    const sides = cfg.sides;
    const sideLength = sideLengthFor(sides);
    const segLength = spec.length;
    const gapSet = new Set(spec.gaps);

    const group = new THREE.Group();
    group.name = `segment.${spec.shape}.${zCenter.toFixed(1)}`;
    group.position.set(0, 0, zCenter);

    // Material is keyed off spec.trick so each variant of a segment
    // shares its own animated material (pulse) or static tint
    // (mirror) without leaking the modulation onto plain segments.
    let material: THREE.MeshStandardMaterial;
    if (spec.trick === "pulse") {
      material = this.getPulseBaseMaterialByShape(spec.shape);
    } else if (spec.trick === "mirror") {
      material = this.getMirrorMaterialByShape(spec.shape);
    } else {
      material = this.getMaterial(spec.shape);
    }
    const geometry = this.getPanelGeometry(sideLength, segLength);
    const panels: PanelInstance[] = [];

    // Panel i sits at angular position alpha = i * (TAU/sides) measured CCW
    // (from +Z looking at the XY plane) starting at the LOCAL -Y direction.
    // So panel 0 is the floor of the cavity, panel 1 is its CCW neighbour,
    // etc. The wall's INNER face touches the inscribed circle at distance
    // APOTHEM; the wall extrudes OUTWARD from there for WALL_THICKNESS.
    const wallCenterDist = APOTHEM + WALL_THICKNESS / 2;
    const tmpFromAxis = new THREE.Vector3(0, 1, 0);
    const tmpOutward = new THREE.Vector3();
    for (let i = 0; i < sides; i++) {
      if (gapSet.has(i)) continue;
      const alpha = (i / sides) * math.TAU;
      const dirX = Math.sin(alpha);
      const dirY = -Math.cos(alpha);
      const cx = dirX * wallCenterDist;
      const cy = dirY * wallCenterDist;

      tmpOutward.set(dirX, dirY, 0);
      const quat = new THREE.Quaternion().setFromUnitVectors(
        tmpFromAxis,
        tmpOutward
      );

      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(cx, cy, 0);
      mesh.quaternion.copy(quat);
      mesh.frustumCulled = true;
      group.add(mesh);

      // STATIC body that we re-pose each frame via syncBodies(). KINEMATIC
      // is the textbook choice for moved-by-script bodies, but it requires
      // a velocity field for proper contact resolution and adds cost we
      // don't need — the player is pinned at world origin so wall surface
      // velocity is irrelevant. STATIC + manual aabbNeedsUpdate is cheaper
      // and detects contacts identically.
      const body = new CANNON.Body({
        mass: 0,
        type: CANNON.Body.STATIC,
        shape: new CANNON.Box(
          new CANNON.Vec3(sideLength / 2, WALL_THICKNESS / 2, segLength / 2)
        ),
      });
      body.allowSleep = false;
      (body as CANNON.Body & { __tag?: string }).__tag = WALL_BODY_TAG;
      (body as CANNON.Body & { __panelIndex?: number }).__panelIndex = i;

      panels.push({ mesh, body, panelIndex: i });
    }

    // Pickups: floating orbs anchored to a panel index + z fraction.
    // We position them slightly INSIDE the inscribed circle so they're
    // grazable by a player rolling on the floor of that panel; for orbs
    // placed in a gap they sit at the same radius and the player has
    // to rotate INTO that gap to grab them (and risk the fall).
    const pickups: PickupInstance[] = [];
    if (spec.pickups && spec.pickups.length > 0) {
      // Distance from tunnel axis to orb center: sit just inside the
      // inscribed circle so the orb visually hovers above the floor
      // panel surface and the player ball (radius 0.45) reaches it.
      const orbDist = APOTHEM - 1.1;
      for (const p of spec.pickups) {
        if (p.panel < 0 || p.panel >= sides) continue;
        const alpha = (p.panel / sides) * math.TAU;
        const dirX = Math.sin(alpha);
        const dirY = -Math.cos(alpha);
        const cx = dirX * orbDist;
        const cy = dirY * orbDist;
        const zLocal = -segLength / 2 + math.clamp(p.z, 0.05, 0.95) * segLength;

        const mesh = createPickupMesh(p.type);
        mesh.position.set(cx, cy, zLocal);
        group.add(mesh);

        const body = new CANNON.Body({
          mass: 0,
          type: CANNON.Body.STATIC,
          shape: new CANNON.Sphere(PICKUP_RADIUS),
          collisionResponse: false,
        });
        body.allowSleep = false;
        (body as CANNON.Body & { __tag?: string }).__tag = PICKUP_BODY_TAG;
        (body as CANNON.Body & { __pickupType?: PickupType }).__pickupType =
          p.type;
        (body as CANNON.Body & { __panelIndex?: number }).__panelIndex =
          p.panel;

        const pickup: PickupInstance = {
          mesh,
          body,
          type: p.type,
          panelIndex: p.panel,
          zLocal,
          consumed: false,
          spin: 0,
        };
        (body as CANNON.Body & { __pickup?: PickupInstance }).__pickup = pickup;
        pickups.push(pickup);
      }
    }

    // Sweep bar: glowing kill-bar that SweepFx tweens between panels in
    // sync with the snare. Built once at construction; SweepFx mutates
    // its position each frame.
    let sweep: SweepInstance | null = null;
    if (spec.trick === "sweep") {
      sweep = this.buildSweep(sideLength, segLength);
      group.add(sweep.mesh);
    }

    return {
      shape: spec.shape,
      group,
      panels,
      pickups,
      speed: spec.speed,
      length: segLength,
      zCenter,
      isGate: false,
      active: false,
      trick: spec.trick ?? null,
      sweep,
      boss: spec.boss ?? null,
      bossEnd: spec.bossEnd === true,
      gaps: [...spec.gaps],
      bank: spec.bank ?? 0,
    };
  }

  // Build the sweep bar mesh + body. Position is left at the origin of
  // the segment group; SweepFx places it on the active panel each frame
  // and syncBodies pushes that into the cannon body.
  private buildSweep(sideLength: number, segLength: number): SweepInstance {
    const geo = this.getSweepGeometry(sideLength, segLength);
    const mat = this.getSweepMaterial();
    const mesh = new THREE.Mesh(geo, mat);
    mesh.frustumCulled = true;

    const body = new CANNON.Body({
      mass: 0,
      type: CANNON.Body.STATIC,
      shape: new CANNON.Box(
        new CANNON.Vec3(sideLength / 2, SWEEP_THICKNESS / 2, segLength * 0.4)
      ),
    });
    body.allowSleep = false;
    (body as CANNON.Body & { __tag?: string }).__tag = WALL_BODY_TAG;
    (body as CANNON.Body & { __sweep?: boolean }).__sweep = true;

    return { mesh, body, panelIndex: -1, lerpAlpha: 0 };
  }

  private getSweepGeometry(
    sideLength: number,
    segLength: number
  ): THREE.BufferGeometry {
    const key = `sweep.${sideLength.toFixed(3)}.${segLength.toFixed(3)}`;
    let g = this.sweepGeometries.get(key);
    if (!g) {
      g = new THREE.BoxGeometry(sideLength, SWEEP_THICKNESS, segLength * 0.8);
      this.sweepGeometries.set(key, g);
    }
    return g;
  }

  private getSweepMaterial(): THREE.MeshStandardMaterial {
    if (this.sweepMaterial) return this.sweepMaterial;
    this.sweepMaterial = new THREE.MeshStandardMaterial({
      color: 0xff3060,
      emissive: 0xff3060,
      emissiveIntensity: 1.6,
      roughness: 0.4,
      metalness: 0.0,
    });
    return this.sweepMaterial;
  }

  buildGate(toShape: Shape, zCenter: number): Segment {
    const cfg = shapeOf(toShape);
    const group = new THREE.Group();
    group.name = `gate.to.${toShape}`;
    group.position.set(0, 0, zCenter);

    const ringGeo = new THREE.TorusGeometry(APOTHEM * 1.05, 0.18, 12, cfg.sides);
    const mat = new THREE.MeshStandardMaterial({
      color: cfg.primary,
      emissive: cfg.primary,
      emissiveIntensity: 1.4,
      roughness: 0.3,
      metalness: 0.0,
    });
    const ring = new THREE.Mesh(ringGeo, mat);
    ring.rotation.z = Math.PI / cfg.sides;
    group.add(ring);

    return {
      shape: toShape,
      group,
      panels: [],
      pickups: [],
      speed: 0,
      length: 1.5,
      zCenter,
      isGate: true,
      active: false,
      trick: null,
      sweep: null,
      boss: null,
      bossEnd: false,
      gaps: [],
      bank: 0,
    };
  }

  attach(segment: Segment, parent: THREE.Object3D): void {
    parent.add(segment.group);
    for (const p of segment.panels) {
      this.physics.addBody(p.body);
      (p.body as CANNON.Body & { __segment?: Segment }).__segment = segment;
    }
    for (const pk of segment.pickups) {
      if (pk.consumed) continue;
      this.physics.addBody(pk.body);
      (pk.body as CANNON.Body & { __segment?: Segment }).__segment = segment;
    }
    if (segment.sweep) {
      this.physics.addBody(segment.sweep.body);
      (segment.sweep.body as CANNON.Body & { __segment?: Segment }).__segment =
        segment;
    }
    segment.active = true;
  }

  detach(segment: Segment): void {
    segment.group.parent?.remove(segment.group);
    for (const p of segment.panels) {
      this.physics.removeBody(p.body);
    }
    for (const pk of segment.pickups) {
      // Pickup bodies always live in the world until the segment is
      // recycled. Consumption only filters them out of broadphase via
      // collisionFilterMask = 0; we MUST NOT removeBody() during a
      // contact callback or cannon-es corrupts its narrowphase state
      // ("wakeUpAfterNarrowphase" of undefined).
      this.physics.removeBody(pk.body);
      disposePickupMesh(pk.mesh);
    }
    segment.pickups.length = 0;
    if (segment.sweep) {
      this.physics.removeBody(segment.sweep.body);
      // The mesh's geometry + material are pooled (sweepGeometries /
      // sweepMaterial) so we don't dispose them here -- only drop the
      // segment-local reference.
      segment.sweep = null;
    }
    segment.active = false;
  }

  syncBodies(segment: Segment): void {
    if (!segment.active) return;
    for (const p of segment.panels) {
      // getWorldPosition / getWorldQuaternion refresh matrixWorld up the
      // parent chain, so body poses are valid the very first frame after
      // a segment is attached, before any render.
      const pos = p.mesh.getWorldPosition(TempVec.vec3());
      const quat = p.mesh.getWorldQuaternion(TempVec.quat());
      p.body.position.set(pos.x, pos.y, pos.z);
      p.body.quaternion.set(quat.x, quat.y, quat.z, quat.w);
      // Cannon-es only auto-invalidates AABBs for bodies the solver
      // integrates. STATIC/KINEMATIC bodies that we teleport need this
      // flag so the SAP broadphase recomputes their bounds before the
      // next pair-finding pass.
      p.body.aabbNeedsUpdate = true;
    }
    for (const pk of segment.pickups) {
      if (pk.consumed) continue;
      const pos = pk.mesh.getWorldPosition(TempVec.vec3());
      pk.body.position.set(pos.x, pos.y, pos.z);
      pk.body.aabbNeedsUpdate = true;
    }
    if (segment.sweep) {
      const pos = segment.sweep.mesh.getWorldPosition(TempVec.vec3());
      const quat = segment.sweep.mesh.getWorldQuaternion(TempVec.quat());
      segment.sweep.body.position.set(pos.x, pos.y, pos.z);
      segment.sweep.body.quaternion.set(quat.x, quat.y, quat.z, quat.w);
      segment.sweep.body.aabbNeedsUpdate = true;
    }
  }

  // Animate pickup orbs: gentle continuous spin + idle bob. Cheap; called
  // once per frame by the streamer for every active segment.
  animatePickups(segment: Segment, dt: number, elapsed: number): void {
    if (!segment.active || segment.pickups.length === 0) return;
    for (const pk of segment.pickups) {
      if (pk.consumed) continue;
      pk.spin += dt * 2.4;
      pk.mesh.rotation.set(pk.spin * 0.6, pk.spin, pk.spin * 0.4);
      // Cache the orb's anchor Y on first animate so the bob oscillates
      // around the spawn position instead of integrating drift.
      const ud = pk.mesh.userData as { anchorY?: number };
      if (ud.anchorY === undefined) ud.anchorY = pk.mesh.position.y;
      const bob = Math.sin(elapsed * 4 + pk.zLocal * 0.3) * 0.08;
      pk.mesh.position.y = ud.anchorY + bob;
    }
  }

  // PickupManager calls this when an orb is consumed. We mark + hide
  // immediately, but DO NOT remove the cannon body here -- this is
  // called from inside Player.onBeginContact, which fires DURING
  // world.step. Removing a body mid-step corrupts cannon-es's
  // narrowphase ("Cannot read properties of undefined (reading
  // 'wakeUpAfterNarrowphase')"). Setting the broadphase mask to 0
  // takes the body out of pair-finding without touching world.bodies.
  // detach() removes the body when the segment recycles.
  consumePickup(pickup: PickupInstance): void {
    if (pickup.consumed) return;
    pickup.consumed = true;
    pickup.mesh.visible = false;
    pickup.body.collisionFilterMask = 0;
  }

  private getPanelGeometry(sideLength: number, segLength: number): THREE.BufferGeometry {
    const key = `panel.${sideLength.toFixed(3)}.${segLength.toFixed(3)}`;
    let g = this.geometryCache.get(key);
    if (!g) {
      g = new THREE.BoxGeometry(sideLength, WALL_THICKNESS, segLength);
      this.geometryCache.set(key, g);
    }
    return g;
  }

  private getMaterial(shape: Shape): THREE.MeshStandardMaterial {
    let m = this.materials.get(shape);
    if (m) return m;
    const cfg = shapeOf(shape);
    const tex = cfg.textureKey ? this.assets.tryGet<THREE.Texture>(cfg.textureKey) : null;
    m = new THREE.MeshStandardMaterial({
      color: cfg.primary,
      emissive: cfg.emissive,
      // Dim baseline -- the tunnel walls themselves should sit JUST
      // above black so the bloom pass doesn't blow out the whole
      // cylinder. Only the active floor tile gets boosted (via its
      // swapped-in highlight material) to read as the lit landing
      // pad. See TileHighlight.
      emissiveIntensity: 0.18,
      roughness: 0.55,
      metalness: 0.05,
      map: tex ?? undefined,
    });
    this.materials.set(shape, m);
    return m;
  }

  dispose(): void {
    for (const m of this.materials.values()) m.dispose();
    for (const m of this.highlightMaterials.values()) m.dispose();
    for (const m of this.pulseMaterials.values()) m.dispose();
    for (const m of this.mirrorMaterials.values()) m.dispose();
    for (const g of this.geometryCache.values()) g.dispose();
    for (const g of this.sweepGeometries.values()) g.dispose();
    if (this.sweepMaterial) this.sweepMaterial.dispose();
    this.materials.clear();
    this.highlightMaterials.clear();
    this.pulseMaterials.clear();
    this.mirrorMaterials.clear();
    this.geometryCache.clear();
    this.sweepGeometries.clear();
    this.sweepMaterial = null;
  }
}
